<template>
    <div v-if="!initDone" class="columns is-centered">
        <LoadingSpinner2 class="has-added-margin" />
    </div>

    <template v-else-if="initDone">
        <div class="columns">
            <div class="column">
                <CategoriesOptionsBox />

                <div v-if="rootNode?.id" class="categories-toolbar">
                    <div class="buttons mb-0">
                        <button class="button" @click="expandAllNodes">
                            <span class="icon">
                                <i class="fa fa-expand-alt" />
                            </span>
                            <span>
                                {{ translate("page.category_manager.expand_all") }}
                            </span>
                        </button>

                        <button class="button" @click="collapseAllNodes">
                            <span class="icon">
                                <i class="fa fa-compress-alt" />
                            </span>
                            <span>
                                {{ translate("page.category_manager.collapse_all") }}
                            </span>
                        </button>
                    </div>

                    <div id="add-categories-toolbar-end" class="buttons mb-0 categories-toolbar__end">
                        <AddCategoriesMenu @select="onAddCategoriesSelect" />

                        <button class="button is-danger" @click="openStartOverModal">
                            <span class="icon">
                                <i class="fa fa-trash-alt" />
                            </span>
                            <span>
                                {{ translate("page.category_manager.start_over") }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <Notification v-if="rootNode?.id && store.undisciplinedLeafCount > 0" class="is-warning mb-4" :centered="false">
            <span class="icon"><i class="fas fa-exclamation-triangle"></i></span>
            {{ translate("page.category_manager.undisciplined_leaf_warning", false, [store.undisciplinedLeafCount]) }}
        </Notification>

        <div class="columns" v-if="rootNode?.id">
            <div class="column">
                <div class="content">
                    <ol>
                        <TreeNode :node="rootNode" :breadcrumb="[]" />
                    </ol>
                </div>
            </div>
        </div>
    </template>

    <ChangeParameterSettings ref="changeParameterSettingsModal" />
    <DeleteParameterTreeModal ref="deleteParameterTreeModal" />
    <StartOverModal ref="startOverModal" />
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, nextTick } from "vue";
    import TreeNode from "./TreeNode.vue";
    import ChangeParameterSettings from "./ChangeParameterSettings.vue";
    import { CHANGE_PARAMETER_SETTINGS, DELETE_PARAMETER_TREE, OPEN_IMPORT_FROM_PAST_EVENT_MODAL, eventBus } from "./eventBus";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Notification from "../Common/Notification.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import DeleteParameterTreeModal from "./DeleteParameterTreeModal.vue";
    import StartOverModal from "./StartOverModal.vue";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import type { ComponentExposed } from "vue-component-type-helpers";
    import CategoriesOptionsBox from "./CategoriesOptionsBox.vue";
    import AddCategoriesMenu from "./AddCategoriesMenu.vue";
    import { useFeatureAnnouncement } from "../Common/FeatureAnnouncements/useFeatureAnnouncement";

    const props = defineProps<{
        eventId: number;
        categoryTranslationKeys: Record<string, string>;
    }>();

    const store = useCategoriesManagerStore();

    store.eventId = props.eventId;
    store.categoryTranslationKeys = props.categoryTranslationKeys;

    const initDone = ref(false);
    const changeParameterSettingsModal = ref<ComponentExposed<typeof ChangeParameterSettings> | null>(null);
    const deleteParameterTreeModal = ref<ComponentExposed<typeof DeleteParameterTreeModal> | null>(null);
    const startOverModal = ref<ComponentExposed<typeof StartOverModal> | null>(null);

    const rootNode = computed(() => store.rootNode);

    function expandAllNodes() {
        store.expandAllNodes();
    }

    function collapseAllNodes() {
        store.collapseAllNodes();
    }

    function openChangeParameterSettingsModal(parameterId: number) {
        changeParameterSettingsModal.value?.open(parameterId);
    }

    function openDeleteParameterTreeModal(parameterId: number) {
        deleteParameterTreeModal.value?.open(parameterId);
    }

    function onAddCategoriesSelect(key: "template" | "event-rules" | "previous-event") {
        if (key === "template") {
            store.navigateTo("import-template");
            store.appendMode = true;
            return;
        }
        if (key === "previous-event") {
            eventBus.$emit(OPEN_IMPORT_FROM_PAST_EVENT_MODAL);
            return;
        }
        if (key === "event-rules") {
            store.navigateTo("extract-from-rules");
            store.appendMode = true;
        }
    }

    function openStartOverModal() {
        startOverModal.value?.open();
    }

    onMounted(() => {
        initDone.value = false;
        eventBus.$on(CHANGE_PARAMETER_SETTINGS, openChangeParameterSettingsModal);
        eventBus.$on(DELETE_PARAMETER_TREE, openDeleteParameterTreeModal);

        return store
            .refreshCategories()
            .finally(() => {
                initDone.value = true;

                const currentUserId = store.currentUserId;
                if (currentUserId && rootNode.value?.id) {
                    nextTick(() => {
                        useFeatureAnnouncement().showEligible(currentUserId, [
                            { key: "category_manager_add_categories_menu", element: "#add-categories-toolbar-end" },
                        ]);
                    });
                }
            })
            .catch(handleError);
    });
</script>

<style scoped>
    .categories-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
    }

    .categories-toolbar__end {
        margin-left: auto;
    }

    @media screen and (max-width: 768px) {
        .categories-toolbar__end {
            margin-left: 0;
        }
    }
</style>
